import { useCallback, useRef, useState } from 'react';
import { toast } from '@/lib/toast';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { UserItem, ItemVisibility } from '@/types/userItem';
import { hasOnlyStockPhoto, STOCK_PHOTO_BLOCK_TITLE, STOCK_PHOTO_BLOCK_DESC } from '@/lib/listingEligibility';
import { hasPendingCollectrPrice, applyCollectrPriceBulk } from '@/lib/collectrPricing';
import { formatUsd } from '@/lib/formatMoney';
import { activity } from '@/store/activityStore';
import { patchItemsInCaches, restoreItemCaches } from '@/lib/optimisticItems';


const BULK_ACTIVITY_ID = 'inventory-bulk-action';

export interface BulkProgressState {
  label: string;
  done: number;
  total: number;
  failed: number;
}

interface UseInventoryBulkActionsArgs {
  filteredItems: UserItem[];
  selectedItemIds: Set<string>;
  clearSelection: () => void;
  setShowBulkListDialog: (open: boolean) => void;
  setShowBulkPriceEditDialog: (open: boolean) => void;
  setBulkListScope?: (scope: 'selection' | 'filtered') => void;
}

export function useInventoryBulkActions({
  filteredItems,
  selectedItemIds,
  clearSelection,
  setShowBulkListDialog,
  setShowBulkPriceEditDialog,
  setBulkListScope,
}: UseInventoryBulkActionsArgs) {
  const queryClient = useQueryClient();
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);

  /**
   * Inline (Tier 2) progress for the bulk bar + the thin global bar (Tier 3).
   * Toasts alone are invisible on mobile, so every multi-step bulk action
   * drives this instead of relying on `toast.loading`.
   */
  const [bulkProgress, setBulkProgress] = useState<BulkProgressState | null>(null);
  const progressRef = useRef<BulkProgressState | null>(null);

  const startProgress = useCallback((label: string, total: number) => {
    const next = { label, done: 0, total, failed: 0 };
    progressRef.current = next;
    setBulkProgress(next);
    activity.start({ id: BULK_ACTIVITY_ID, label, done: 0, total, failed: 0 });
  }, []);

  const tickProgress = useCallback((ok: boolean) => {
    const cur = progressRef.current;
    if (!cur) return;
    const next = { ...cur, done: cur.done + 1, failed: cur.failed + (ok ? 0 : 1) };
    progressRef.current = next;
    setBulkProgress(next);
    activity.update(BULK_ACTIVITY_ID, { done: next.done, failed: next.failed });
  }, []);

  const endProgress = useCallback(() => {
    progressRef.current = null;
    setBulkProgress(null);
    activity.end(BULK_ACTIVITY_ID);
  }, []);

  // Bulk LIST — only flips items that are NOT already listed AND have a price OR accept offers
  const handleSelectedListForSale = useCallback(async () => {
    if (selectedItemIds.size === 0) return;
    const selectedItems = filteredItems.filter(item => selectedItemIds.has(item.id));
    const alreadyListed = selectedItems.filter(i => i.visibility === 'listed').length;
    const candidates = selectedItems.filter(i => i.visibility !== 'listed');
    const stockBlocked = candidates.filter(hasOnlyStockPhoto);
    const eligible = candidates.filter(
      i => !hasOnlyStockPhoto(i) && ((i.asking_price_cents ?? 0) > 0 || !!i.is_negotiable),
    );
    const skippedNoPrice = candidates.length - stockBlocked.length - eligible.length;

    if (candidates.length === 0) {
      if (alreadyListed === selectedItems.length) {
        toast.info('All selected items are already listed.');
      } else if (stockBlocked.length > 0) {
        toast.error(STOCK_PHOTO_BLOCK_TITLE, { description: STOCK_PHOTO_BLOCK_DESC });
      }
      return;
    }

    // If any candidate is missing a price AND offers, hand off to BulkListDialog
    // so the user can set a default price or enable "accept offers" in one step.
    if (eligible.length < candidates.filter(i => !hasOnlyStockPhoto(i)).length) {
      setBulkListScope?.('selection');
      setShowBulkListDialog(true);
      return;
    }

    if (eligible.length === 0) {
      toast.error('Set a price or turn on offers first — none of the selected items are list-ready.');
      return;
    }



    setIsBulkProcessing(true);
    const listSnapshot = patchItemsInCaches(queryClient, eligible.map(i => i.id), {
      visibility: 'listed',
      is_listed: true,
    });
    try {
      const { error } = await supabase
        .from('user_items')
        .update({ visibility: 'listed', is_listed: true })
        .in('id', eligible.map(i => i.id));
      if (error) throw error;
      queryClient.invalidateQueries({ queryKey: ['my-items'] });
      const parts = [`${eligible.length} item${eligible.length !== 1 ? 's' : ''} successfully listed`];
      if (alreadyListed > 0) parts.push(`${alreadyListed} already listed`);
      if (skippedNoPrice > 0) parts.push(`${skippedNoPrice} skipped (no price or offers)`);
      if (stockBlocked.length > 0) parts.push(`${stockBlocked.length} skipped (stock photo only)`);
      toast.success(parts.join(' · '));
      clearSelection();
    } catch (e) {
      restoreItemCaches(queryClient, listSnapshot);
      console.error(e);

      toast.error('Failed to list selected items');
    } finally {
      setIsBulkProcessing(false);
    }
  }, [selectedItemIds, filteredItems, queryClient, clearSelection, setBulkListScope, setShowBulkListDialog]);

  // Bulk ACCEPT OFFERS
  const handleSelectedAcceptOffers = useCallback(async () => {
    if (selectedItemIds.size === 0) return;
    const selectedItems = filteredItems.filter(item => selectedItemIds.has(item.id));
    const eligible = selectedItems.filter(i => !i.is_negotiable);
    if (eligible.length === 0) {
      toast.info('All selected items are already accepting offers.');
      return;
    }
    setIsBulkProcessing(true);
    const offersSnapshot = patchItemsInCaches(queryClient, eligible.map(i => i.id), {
      is_negotiable: true,
    });
    try {
      const { error } = await supabase
        .from('user_items')
        .update({ is_negotiable: true })
        .in('id', eligible.map(i => i.id));
      if (error) throw error;
      queryClient.invalidateQueries({ queryKey: ['my-items'] });
      const skipped = selectedItems.length - eligible.length;
      const parts = [`${eligible.length} now accepting offers`];
      if (skipped > 0) parts.push(`${skipped} already on`);
      toast.success(parts.join(' · '));
    } catch (e) {
      restoreItemCaches(queryClient, offersSnapshot);
      console.error(e);

      toast.error('Failed to enable offers');
    } finally {
      setIsBulkProcessing(false);
    }
  }, [selectedItemIds, filteredItems, queryClient]);

  // Bulk UNLIST
  const handleSelectedUnlist = useCallback(async () => {
    if (selectedItemIds.size === 0) return;
    const selectedItems = filteredItems.filter(item => selectedItemIds.has(item.id));
    const eligible = selectedItems.filter(i => i.visibility === 'listed');

    if (eligible.length === 0) {
      toast.info('None of the selected items are currently listed.');
      return;
    }

    setIsBulkProcessing(true);
    const unlistSnapshot = patchItemsInCaches(queryClient, eligible.map(i => i.id), {
      visibility: 'private',
      is_listed: false,
    });
    try {
      const { error } = await supabase
        .from('user_items')
        .update({ visibility: 'private', is_listed: false })
        .in('id', eligible.map(i => i.id));
      if (error) throw error;
      queryClient.invalidateQueries({ queryKey: ['my-items'] });
      const skipped = selectedItems.length - eligible.length;
      const parts = [`${eligible.length} unlisted`];
      if (skipped > 0) parts.push(`${skipped} already unlisted`);
      toast.success(parts.join(' · '));
      clearSelection();
    } catch (e) {
      restoreItemCaches(queryClient, unlistSnapshot);
      console.error(e);

      toast.error('Failed to unlist selected items');
    } finally {
      setIsBulkProcessing(false);
    }
  }, [selectedItemIds, filteredItems, queryClient, clearSelection]);

  // Bulk PRICE FROM COLLECTR
  const handleSelectedPriceFromCollectr = useCallback(async () => {
    if (selectedItemIds.size === 0) return;
    const selectedItems = filteredItems.filter(item => selectedItemIds.has(item.id));
    const eligible = selectedItems.filter(i => hasPendingCollectrPrice(i));
    if (eligible.length === 0) {
      toast.info('No Collectr-imported prices available on the selected items.');
      return;
    }
    setIsBulkProcessing(true);
    startProgress('Applying Collectr prices', eligible.length);
    const toastId = toast.loading(`Applying Collectr prices to ${eligible.length} item${eligible.length !== 1 ? 's' : ''}…`);
    try {
      const { applied, skipped } = await applyCollectrPriceBulk(eligible);
      queryClient.invalidateQueries({ queryKey: ['my-items'] });
      const parts = [`${applied} priced from Collectr`];
      const totalSkipped = (selectedItems.length - eligible.length) + skipped;
      if (totalSkipped > 0) parts.push(`${totalSkipped} skipped`);
      toast.success(parts.join(' · '), { id: toastId });
    } catch (e) {
      console.error(e);
      toast.error('Failed to apply Collectr prices', { id: toastId });
    } finally {
      endProgress();
      setIsBulkProcessing(false);
    }
  }, [selectedItemIds, filteredItems, queryClient, startProgress, endProgress]);

  // Filtered-set bulk visibility change (from toolbar dropdown)
  const handleBulkVisibilityChange = async (newVisibility: ItemVisibility) => {
    if (filteredItems.length === 0) {
      toast.error('No items to update');
      return;
    }
    if (newVisibility === 'listed') {
      setShowBulkListDialog(true);
      return;
    }
    setIsBulkProcessing(true);
    const itemIds = filteredItems.map(item => item.id);
    try {
      const { error } = await supabase
        .from('user_items')
        .update({ visibility: newVisibility, is_listed: false })
        .in('id', itemIds);
      if (error) throw error;
      queryClient.invalidateQueries({ queryKey: ['my-items'] });
      toast.success(`${itemIds.length} items set to ${newVisibility}`);
    } catch (error) {
      console.error('Bulk update error:', error);
      toast.error('Failed to update items');
    } finally {
      setIsBulkProcessing(false);
    }
  };

  // Bulk stock images
  const handleBulkStockImages = async () => {
    const itemsNeedingImages = filteredItems.filter(
      item => !item.images?.[0]?.image_url && !item.stock_image_url,
    );
    if (itemsNeedingImages.length === 0) {
      toast.error('No items need images');
      return;
    }

    setIsBulkProcessing(true);
    startProgress('Fetching images', itemsNeedingImages.length);
    let successCount = 0;
    let failCount = 0;
    const toastId = toast.loading(`Fetching images... 0/${itemsNeedingImages.length}`);

    for (let i = 0; i < itemsNeedingImages.length; i++) {
      const item = itemsNeedingImages[i];
      toast.loading(`Fetching images... ${i + 1}/${itemsNeedingImages.length}`, { id: toastId });
      let ok = false;
      try {
        const { data, error } = await supabase.functions.invoke('fetch-stock-image', {
          body: {
            title: item.title,
            set_name: item.set_name,
            card_number: item.card_number,
            category: item.category,
            grading_company: item.grading_company,
            slab_grade: item.slab_grade,
          },
        });
        if (error) {
          console.error('Stock image fetch error:', error);
          failCount++;
        } else if (data?.imageUrl) {
          const { error: updateError } = await supabase
            .from('user_items')
            .update({ stock_image_url: data.imageUrl })
            .eq('id', item.id);
          if (updateError) { console.error('Stock image update error:', updateError); failCount++; }
          else { successCount++; ok = true; }
        } else {
          failCount++;
        }
      } catch (err) {
        console.error('Stock image error:', err);
        failCount++;
      }
      tickProgress(ok);
    }

    toast.dismiss(toastId);
    queryClient.invalidateQueries({ queryKey: ['my-items'] });
    if (successCount > 0) {
      toast.success(`Added images to ${successCount} item${successCount !== 1 ? 's' : ''}${failCount > 0 ? `, ${failCount} failed` : ''}`);
    } else {
      toast.error(`Failed to find images for ${failCount} item${failCount !== 1 ? 's' : ''}`);
    }
    endProgress();
    setIsBulkProcessing(false);
  };

  // Bulk price edit
  const handleBulkPriceEdit = async (mode: 'percentage' | 'fixed', value: number) => {
    const itemsWithPrice = filteredItems.filter(item => item.asking_price_cents && item.asking_price_cents > 0);
    if (itemsWithPrice.length === 0) {
      toast.error('No items with prices to update');
      return;
    }

    setIsBulkProcessing(true);
    startProgress('Updating prices', itemsWithPrice.length);
    let successCount = 0;
    let failCount = 0;
    const toastId = toast.loading(`Updating prices... 0/${itemsWithPrice.length}`);

    for (let i = 0; i < itemsWithPrice.length; i++) {
      const item = itemsWithPrice[i];
      toast.loading(`Updating prices... ${i + 1}/${itemsWithPrice.length}`, { id: toastId });
      const newPrice = mode === 'percentage'
        ? Math.max(1, Math.round(item.asking_price_cents! * (1 + value / 100)))
        : Math.round(value * 100);
      let ok = false;
      try {
        const { error } = await supabase
          .from('user_items')
          .update({ asking_price_cents: newPrice })
          .eq('id', item.id);
        if (error) { console.error('Price update error:', error); failCount++; }
        else { successCount++; ok = true; }
      } catch (err) {
        console.error('Price update error:', err);
        failCount++;
      }
      tickProgress(ok);
    }

    toast.dismiss(toastId);
    queryClient.invalidateQueries({ queryKey: ['my-items'] });
    setShowBulkPriceEditDialog(false);

    if (successCount > 0) {
      const actionText = mode === 'percentage'
        ? (value > 0 ? `increased by ${value}%` : `decreased by ${Math.abs(value)}%`)
        : `set to ${formatUsd(value)}`;
      toast.success(`${successCount} item${successCount !== 1 ? 's' : ''} ${actionText}${failCount > 0 ? `, ${failCount} failed` : ''}`);
    } else {
      toast.error(`Failed to update prices for ${failCount} item${failCount !== 1 ? 's' : ''}`);
    }
    endProgress();
    setIsBulkProcessing(false);
  };

  // Bulk list confirm (from BulkListDialog + price queue).
  //  - `items` scopes to the batch to consider (selection or filtered).
  //  - `enableOffers` = list all unpriced (that aren't overridden) with is_negotiable=true.
  //  - `overrides` = per-item decisions from the price-queue dialog:
  //       'price'  → set asking_price_cents, list
  //       'offers' → set is_negotiable, list without price
  //       'skip'   → leave unlisted
  const handleBulkListConfirm = async (
    defaultPriceCents: number | null,
    enableOffers: boolean,
    items?: UserItem[],
    overrides?: Map<string, { kind: 'price'; priceCents: number; acceptsOffers?: boolean } | { kind: 'offers' } | { kind: 'skip' }>,
  ) => {
    setIsBulkProcessing(true);
    try {
      const source = items ?? filteredItems;
      const eligibleItems = source.filter(item => !hasOnlyStockPhoto(item));
      const stockBlockedCount = source.length - eligibleItems.length;

      // Partition eligible items.
      const alreadyPriced: UserItem[] = [];
      const priceOverrides: { item: UserItem; priceCents: number; acceptsOffers: boolean }[] = [];
      const offersOverrides: UserItem[] = [];
      const skipped: UserItem[] = [];
      const untouchedUnpriced: UserItem[] = [];

      for (const item of eligibleItems) {
        const decision = overrides?.get(item.id);
        if (decision) {
          if (decision.kind === 'price') priceOverrides.push({ item, priceCents: decision.priceCents, acceptsOffers: !!decision.acceptsOffers });
          else if (decision.kind === 'offers') offersOverrides.push(item);
          else skipped.push(item);
          continue;
        }
        if (item.asking_price_cents && item.asking_price_cents > 0) {
          alreadyPriced.push(item);
        } else {
          untouchedUnpriced.push(item);
        }
      }

      // 1. Priced already → just flip visibility.
      if (alreadyPriced.length > 0) {
        const { error } = await supabase
          .from('user_items')
          .update({ visibility: 'listed' as ItemVisibility, is_listed: true })
          .in('id', alreadyPriced.map(i => i.id));
        if (error) throw error;
      }

      // 2. Priced via overrides → set asking price + list, grouped by (price, acceptsOffers).
      if (priceOverrides.length > 0) {
        const byKey = new Map<string, { priceCents: number; acceptsOffers: boolean; ids: string[] }>();
        for (const { item, priceCents, acceptsOffers } of priceOverrides) {
          const key = `${priceCents}::${acceptsOffers ? 1 : 0}`;
          const bucket = byKey.get(key) ?? { priceCents, acceptsOffers, ids: [] };
          bucket.ids.push(item.id);
          byKey.set(key, bucket);
        }
        for (const { priceCents, acceptsOffers, ids } of byKey.values()) {
          const { error } = await supabase
            .from('user_items')
            .update({
              visibility: 'listed' as ItemVisibility,
              is_listed: true,
              asking_price_cents: priceCents,
              ...(acceptsOffers ? { is_negotiable: true } : {}),
            })
            .in('id', ids);
          if (error) throw error;
        }
      }

      // 3. Offers-only (from queue).
      if (offersOverrides.length > 0) {
        const { error } = await supabase
          .from('user_items')
          .update({
            visibility: 'listed' as ItemVisibility,
            is_listed: true,
            is_negotiable: true,
          })
          .in('id', offersOverrides.map(i => i.id));
        if (error) throw error;
      }

      // 4. Untouched unpriced items — fall back to defaultPrice / enableOffers legacy flags.
      if (untouchedUnpriced.length > 0) {
        if (defaultPriceCents) {
          const { error } = await supabase
            .from('user_items')
            .update({
              visibility: 'listed' as ItemVisibility,
              is_listed: true,
              asking_price_cents: defaultPriceCents,
            })
            .in('id', untouchedUnpriced.map(i => i.id));
          if (error) throw error;
        } else if (enableOffers) {
          const { error } = await supabase
            .from('user_items')
            .update({
              visibility: 'listed' as ItemVisibility,
              is_listed: true,
              is_negotiable: true,
            })
            .in('id', untouchedUnpriced.map(i => i.id));
          if (error) throw error;
        }
      }

      queryClient.invalidateQueries({ queryKey: ['my-items'] });
      const listedCount =
        alreadyPriced.length +
        priceOverrides.length +
        offersOverrides.length +
        (defaultPriceCents || enableOffers ? untouchedUnpriced.length : 0);
      const parts = [`${listedCount} item${listedCount !== 1 ? 's' : ''} successfully listed`];
      if (skipped.length > 0) parts.push(`${skipped.length} skipped`);
      if (stockBlockedCount > 0) parts.push(`${stockBlockedCount} skipped (stock photo only)`);
      toast.success(parts.join(' · '), stockBlockedCount > 0 ? { description: STOCK_PHOTO_BLOCK_DESC } : undefined);
      setShowBulkListDialog(false);
    } catch (error) {
      console.error('Bulk list error:', error);
      toast.error('Failed to list items');
    } finally {
      setIsBulkProcessing(false);
    }
  };

  return {
    isBulkProcessing,
    bulkProgress,
    handleSelectedListForSale,
    handleSelectedAcceptOffers,
    handleSelectedUnlist,
    handleSelectedPriceFromCollectr,
    handleBulkVisibilityChange,
    handleBulkStockImages,
    handleBulkPriceEdit,
    handleBulkListConfirm,
  };
}
